import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "A Fast Mutex Lamport Lock with JavaScript Promises",
  "description": "",
  "published": "2017-03-30T00:00:00.000Z",
  "redirect_from": ["/blog/fast-mutex-lamport-lock-javascript-promises/swizec/7532"],
  "categories": "Front End, Technical",
  "hero": "../../../defaultHero.jpg"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <lite-youtube {...{
      "videoid": "NQqDJoLqMtE",
      "videostartat": "0"
    }}></lite-youtube>
    <p>{`I'm trying something new. Today the video is stronger than the post. Watch it ?`}</p>
    <p>{`On Tuesday, I implemented a JavaScript queue backed by local storage. It's goal in life is to ensure we don't lose events when tracking usage funnels on our app.`}</p>
    <p>{`Yes, losing an event that takes 600ms to send is an edge case.`}</p>
    <p>{`Yes, we still want to make sure we don't lose anything.`}</p>
    <p>{`Plus, with a queue we have more flexibility to send bursts of events to our backend. Firing an XHR request every time a user clicks a button feels excessive.`}</p>
    <p>{`That's why it's going in a queue, and the queue will eventually process it. At first, 1 event is 1 request, and we have a good path forward to send in batches.`}</p>
    <p>{`Because we store the queue in local storage, you don't lose events even if you close your tab before something finishes saving. Hopefully you come back a second time so we get a chance to process your queue. If you don't, we can't. ?`}</p>
    <p>{`Simple, right?`}</p>
    <p>{`Yep. Stringify a JSON array. Save to local storage. Pull out, JSON parse, add stuff, put back in.`}</p>
    <p>{`And what if the same user runs multiple tabs? What if some events that you want to track happen without user interaction? And just like that, tabs step on each other's toes, and you lose events because local storage is a shared resource.`}</p>
    <h2 {...{
      "id": "fast-mutex-to-the-rescue"
    }}>{`Fast mutex to the rescue!`}</h2>
    <p>{`Here's how the `}<a parentName="p" {...{
        "href": "http://www.cs.rochester.edu/research/synchronization/pseudocode/fastlock.html"
      }}>{`Lamport lock algorithm`}</a>{` works:`}</p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2017/03/algorithm-pic.png",
        "alt": null
      }}></img></p>
    <p>{`You need 2 shared memory locations, `}<inlineCode parentName="p">{`A`}</inlineCode>{` and `}<inlineCode parentName="p">{`B`}</inlineCode>{`.`}</p>
    <p>{`When a new thread needs the lock, it first sets `}<inlineCode parentName="p">{`A`}</inlineCode>{` to its `}<inlineCode parentName="p">{`id`}</inlineCode>{`. Then it checks if `}<inlineCode parentName="p">{`B`}</inlineCode>{` is free. If `}<inlineCode parentName="p">{`B`}</inlineCode>{` is `}<em parentName="p">{`not`}</em>{` free, we retry. If `}<inlineCode parentName="p">{`B`}</inlineCode>{` `}<em parentName="p">{`is`}</em>{` free, we set `}<inlineCode parentName="p">{`B`}</inlineCode>{` to `}<inlineCode parentName="p">{`id`}</inlineCode>{`. Then we check that `}<inlineCode parentName="p">{`A`}</inlineCode>{` is still equal to `}<inlineCode parentName="p">{`id`}</inlineCode>{`.`}</p>
    <p>{`If `}<inlineCode parentName="p">{`A`}</inlineCode>{` has changed, we're potentially facing a lock contention. So we wait long enough for others to realize we're doing stuff and back off. Then we check that `}<inlineCode parentName="p">{`B`}</inlineCode>{` is still `}<inlineCode parentName="p">{`id`}</inlineCode>{`. If `}<inlineCode parentName="p">{`B`}</inlineCode>{` is `}<em parentName="p">{`not`}</em>{` equal to `}<inlineCode parentName="p">{`id`}</inlineCode>{`, then we've lost the lock and we go back to beginning. If `}<inlineCode parentName="p">{`B`}</inlineCode>{` `}<em parentName="p">{`is`}</em>{` equal to `}<inlineCode parentName="p">{`id`}</inlineCode>{`, it means we've won the lock and we do stuff.`}</p>
    <p>{`It sounds confusing because it `}<em parentName="p">{`is`}</em>{` confusing. Study the flowchart. Drawing it helped me understand how this works. And I'm not sure I could `}<em parentName="p">{`prove`}</em>{` that it works.`}</p>
    <p>{`Here's what the Lamport lock looks like when implemented with JavaScript promises.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`get lock() {
    return this._lock(0);
}

_lock(retries) {
    const A = \`\${this.name}_lock_A\`,
          B = \`\${this.name}_lock_B\`;

    const _retry = (resolve, reject) => {
        window.requestAnimationFrame(() =>
            this._lock(retries + 1).then(resolve, reject)
        );
    }

    const _free = (name) => {
        const val = this._getItem(name);
        return Number(val) === 0 || val === undefined || val === null;
    }

    this._setItem(A, this.id);

    return new Promise((resolve, reject) => {
        // retries > 10, force lock
        if (_free(B) || retries > 10) {
            this._setItem(B, this.id);

            if (this._getItem(A) === this.id) {
                // no contention, do stuff
                resolve();
                this._setItem(B, 0);
            }else{
                // possible contention
                window.requestAnimationFrame(() => {
                    if (this._getItem(B) === this.id) {
                        // won lock, do stuff
                        resolve();
                        this._setItem(B, 0);
                    }else{
                        _retry(resolve, reject);
                    }
                });
            }
        }else{
            _retry(resolve, reject);
        }
    });
}
`}</code></pre>
    <p>{`Done with promises so it's easier to use. You'd do something like this when writing to local storage:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`this.lock.then(() => writeStuff());
`}</code></pre>
    <p>{`That's elegant, right? I think it is.`}</p>
    <p>{`But what does all that lock code `}<em parentName="p">{`do`}</em>{`? Well, it implements the Lamport lock algorithm with one small addition.`}</p>
    <p>{`Our threads – browser tabs – can die before releasing the lock. The user closes the tab, JavaScript error, things like that. That's why we limit retries to `}<inlineCode parentName="p">{`<10`}</inlineCode>{`.`}</p>
    <p>{`Yes, that's an arbitrary value. No, saving to local storage should never take more than 10 requestAnimationFrames. It's a fast, synchronous operation.`}</p>
    <p>{`Look at the code and the flowchart side-by-side. I made sure they're the same ?`}</p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2017/03/together-screenshot.png",
        "alt": null
      }}></img></p>
    <p>{`Don't believe me that it works? `}<a parentName="p" {...{
        "href": "https://www.microsoft.com/en-us/research/publication/fast-mutual-exclusion-algorithm/?from=http://research.microsoft.com/en-us/um/people/lamport/pubs/fast-mutex.pdf"
      }}>{`Read Lamport's original paper`}</a>{`; it includes correctness proofs.`}</p>
    <p>{`Watch `}<a parentName="p" {...{
        "href": "https://youtu.be/NQqDJoLqMtE"
      }}>{`the video`}</a>{` to see it in action.`}</p>
    <p>{`Thanks to `}<a parentName="p" {...{
        "href": "https://medium.engineering/wait-dont-touch-that-a211832adc3a"
      }}>{`Dan Pupius for the idea`}</a>{`.`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      